/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {Datatable} from '../../../../components/Datatable/Datatable'
import {Loading} from '../../../../components/Loading/Loading'
import {GET, POST, PUT} from '../../../../services/api'
import {useNavigate} from 'react-router-dom'
import {i18n} from '../../../../translate/i18n'
import {BreadCrumbs} from '../../../../components/BreadCrumbs/breadCrumbrs'
import {useSwitchStatus} from '../../../../components/Datatable/SwitchStatus'
import Swal from 'sweetalert2'
import styles from '../../ContentStructured/CleanSafe/cleanSafe.module.css'

type EmailTargetType = {
  id: string
  targetId: number
  group: string
  name: string
  activeEmail: boolean
  endpoint:
    | 'status-hotel'
    | 'room-service'
    | 'hotel-service'
    | 'post'
    | 'hotel-tours'
    | 'hotel-activities'
    | 'hotel-experiences'
}

const HotelSmsContent: FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const {switchStatus} = useSwitchStatus()
  const [list, setList] = useState<any[]>([])
  const [activeTab, setActiveTab] = useState('#tab1')
  const [tabErrors] = useState<{[key: string]: boolean}>({
    tab1: false,
    tab2: false,
  })
  const [loadingEmail, setLoadingEmail] = useState(false)
  const [emailTargets, setEmailTargets] = useState<EmailTargetType[]>([])
  const [emailPage, setEmailPage] = useState(1)
  const [emailTake, setEmailTake] = useState(50)

  const getTabClass = (tabId: string, isActive: boolean) => {
    const baseClass = styles.tabItem

    if (tabErrors[tabId]) {
      return `${baseClass} ${styles.tabError} ${isActive ? 'active' : ''}`
    }

    return `${baseClass} ${isActive ? styles.tabActive : styles.tabInactive}`
  }

  const parseRows = (response: any) => {
    const payload = response?.data

    if (Array.isArray(payload)) return payload
    if (Array.isArray(payload?.data)) return payload.data
    if (payload?.data) return [payload.data]

    return []
  }

  const getEmailTargets = async () => {
    try {
      setLoadingEmail(true)

      const [
        statusHotelRes,
        roomServiceRes,
        hotelServiceRes,
        reservationPostRes,
        servicePostRes,
        hotelToursRes,
        hotelActivitiesRes,
        hotelExperiencesRes,
      ] = await Promise.allSettled([
        GET('/status-hotel'),
        GET('/room-service'),
        POST('/hotel-service/filter', {classificationId: 0}),
        POST('/post/filter', {type: 'hotel_reservation'}),
        POST('/post/filter', {type: 'hotel_service'}),
        GET('/hotel-tours'),
        GET('/hotel-activities'),
        GET('/hotel-experiences'),
      ])

      const statusRows =
        statusHotelRes.status === 'fulfilled' ? parseRows(statusHotelRes.value) : []
      const roomServiceRows =
        roomServiceRes.status === 'fulfilled' ? parseRows(roomServiceRes.value) : []
      const hotelServiceRows =
        hotelServiceRes.status === 'fulfilled' ? parseRows(hotelServiceRes.value) : []
      const reservationRows =
        reservationPostRes.status === 'fulfilled' ? parseRows(reservationPostRes.value) : []
      const servicePostRows =
        servicePostRes.status === 'fulfilled' ? parseRows(servicePostRes.value) : []
      const hotelToursRows =
        hotelToursRes.status === 'fulfilled' ? parseRows(hotelToursRes.value) : []
      const hotelActivitiesRows =
        hotelActivitiesRes.status === 'fulfilled' ? parseRows(hotelActivitiesRes.value) : []
      const hotelExperiencesRows =
        hotelExperiencesRes.status === 'fulfilled' ? parseRows(hotelExperiencesRes.value) : []

      // id unico por linha: o Datatable deduplica por id na busca interna
      let rowIndex = 0
      const mapEmailTarget = (
        item: any,
        group: string,
        endpoint: EmailTargetType['endpoint']
      ): EmailTargetType => ({
        id: `${endpoint}-${item.id}-${rowIndex++}`,
        targetId: Number(item.id),
        group,
        name: item.name ?? item.title,
        activeEmail: Boolean(item.activeEmail),
        endpoint,
      })

      const rows: EmailTargetType[] = [
        ...statusRows.map((item: any) =>
          mapEmailTarget(item, String(i18n.t(`sms.email_group_status`)), 'status-hotel')
        ),
        ...roomServiceRows.map((item: any) =>
          mapEmailTarget(item, String(i18n.t(`sms.email_group_service_forms`)), 'room-service')
        ),
        ...hotelServiceRows.map((item: any) =>
          mapEmailTarget(item, String(i18n.t(`sms.email_group_services`)), 'hotel-service')
        ),
        ...reservationRows.map((item: any) =>
          mapEmailTarget(item, String(i18n.t(`sms.email_group_services_booking`)), 'post')
        ),
        ...servicePostRows.map((item: any) =>
          mapEmailTarget(item, String(i18n.t(`sms.email_group_services_booking`)), 'post')
        ),
        ...hotelToursRows.map((item: any) =>
          mapEmailTarget(item, String(i18n.t(`sms.email_group_tours`)), 'hotel-tours')
        ),
        ...hotelActivitiesRows.map((item: any) =>
          mapEmailTarget(item, String(i18n.t(`sms.email_group_activities`)), 'hotel-activities')
        ),
        ...hotelExperiencesRows.map((item: any) =>
          mapEmailTarget(item, String(i18n.t(`sms.email_group_experiences`)), 'hotel-experiences')
        ),
      ]

      const groupOrder: {[key: string]: number} = {
        [String(i18n.t(`sms.email_group_status`))]: 1,
        [String(i18n.t(`sms.email_group_service_forms`))]: 2,
        [String(i18n.t(`sms.email_group_services`))]: 3,
        [String(i18n.t(`sms.email_group_services_booking`))]: 4,
        [String(i18n.t(`sms.email_group_tours`))]: 5,
        [String(i18n.t(`sms.email_group_activities`))]: 6,
        [String(i18n.t(`sms.email_group_experiences`))]: 7,
      }

      const sortedRows = [...rows].sort((a, b) => {
        const byGroup = (groupOrder[a.group] ?? 99) - (groupOrder[b.group] ?? 99)
        if (byGroup !== 0) return byGroup
        return String(a.name).localeCompare(String(b.name))
      })

      setEmailTargets(sortedRows)
      setEmailPage(1)
      setLoadingEmail(false)
    } catch (e) {
      setLoadingEmail(false)
      console.log(e)
    }
  }

  const switchEmailStatus = async (item: EmailTargetType) => {
    try {
      setLoadingEmail(true)
      const response = await PUT(`/${item.endpoint}/${item.targetId}`, {
        activeEmail: !item.activeEmail,
      })

      if (response?.success !== true) {
        Swal.fire({
          icon: 'error',
          title: String(i18n.t(`general.error`)),
          text: String(i18n.t(`sms.status_update_error`)),
        })
      }

      await getEmailTargets()
      setLoadingEmail(false)
    } catch (e) {
      setLoadingEmail(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t(`general.error`)),
        text: String(i18n.t(`sms.status_update_error`)),
      })
    }
  }

  const getStatusHotel = async (showLoading = true) => {
    try {
      setLoading(showLoading)
      const response = await GET('/status-hotel')
      const payload = response?.data
      const rows = Array.isArray(payload) ? payload : payload?.data ?? []

      const mappedList = rows.map((row: any) => {
        return {
          id: row.id,
          order: row.order,
          status: Boolean(row.status),
          name: row.name,
          messageStatus: row.messageStatus,
        }
      })

      setList(mappedList)
      setLoading(false)
    } catch (e) {
      setLoading(false)
      console.log(e)
    }
  }

  useEffect(() => {
    getStatusHotel()
    getEmailTargets()
  }, [])

  const emailPageCount = Math.max(1, Math.ceil(emailTargets.length / emailTake) || 1)
  const safeEmailPage = Math.min(emailPage, emailPageCount)
  const emailMeta = {
    page: safeEmailPage,
    take: emailTake,
    itemCount: emailTargets.length,
    pageCount: emailPageCount,
    hasPreviousPage: safeEmailPage > 1,
    hasNextPage: safeEmailPage < emailPageCount,
  }
  const pagedEmailTargets = emailTargets.slice(
    (safeEmailPage - 1) * emailTake,
    safeEmailPage * emailTake
  )

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>{i18n.t(`sms.status_hotel`)}</span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t(`sms.listened_status_hotel`)}
                </span>
              </h3>
            </div>
            <div className='card-body'>
              <div className='d-flex justify-content-between'>
                <div className={`d-flex ${styles.tabMenu}`}>
                  <div
                    className={getTabClass('tab1', activeTab === '#tab1')}
                    onClick={() => setActiveTab('#tab1')}
                  >
                    <i className='fas fa-comment-alt me-2'></i>
                    {i18n.t(`sms.status_label`)}
                  </div>
                  <div
                    className={getTabClass('tab2', activeTab === '#tab2')}
                    onClick={() => setActiveTab('#tab2')}
                  >
                    <i className='fas fa-envelope me-2'></i>
                    {i18n.t(`sms.notification`)}
                  </div>
                </div>
              </div>

              <div className='mt-5'>
                {activeTab === '#tab1' && (
                  <Datatable
                    data={list}
                    showControls={false}
                    headers={[
                      {key: i18n.t(`sms.order`), value: 'order', type: 'default', noOrder: true},
                      {key: i18n.t(`sms.name`), value: 'name', type: 'default', noOrder: true},
                      {
                        key: i18n.t(`sms.message`),
                        value: 'messageStatus',
                        type: 'default',
                        noOrder: true,
                      },
                      {
                        key: i18n.t(`sms.status_label`),
                        value: 'status',
                        type: 'check',
                        action: async (item) => {
                          await switchStatus({
                            endpoint: 'status-hotel',
                            Id: item.id,
                            status: item.status,
                            statusKey: 'status',
                          })
                          await getStatusHotel(false)
                        },
                        noOrder: true,
                      },
                      {key: i18n.t(`general.actions`), value: 'acoes', type: '', noOrder: true},
                    ]}
                    options={[
                      {
                        icon: 'fas fa-pen',
                        action: (item) => {
                          navigate(`edit/${item.id}`)
                        },
                        title: i18n.t(`sms.edit_status`),
                      },
                    ]}
                    color='primary'
                  />
                )}

                {activeTab === '#tab2' &&
                  (loadingEmail ? (
                    <Loading />
                  ) : (
                    <Datatable
                      data={pagedEmailTargets}
                      meta={emailMeta}
                      onChangeData={async (take: number, page: number) => {
                        setEmailTake(take)
                        setEmailPage(page > 0 ? page : 1)
                      }}
                      headers={[
                        {
                          key: String(i18n.t(`general.type`)),
                          value: 'group',
                          type: 'default',
                          noOrder: true,
                        },
                        {
                          key: String(i18n.t(`sms.name`)),
                          value: 'name',
                          type: 'default',
                          noOrder: true,
                        },
                        {
                          key: String(i18n.t(`sms.send_email_simple`)),
                          value: 'activeEmail',
                          type: 'check',
                          action: (item) => switchEmailStatus(item),
                          noOrder: true,
                        },
                      ]}
                      color='primary'
                    />
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const HotelSms: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: true},
          {title: i18n.t(`sms.status_hotel`), path: 'configuracoes/message_hotel', isActive: true},
        ]}
      >
        {i18n.t(`sms.status_hotel`)}
      </PageTitle>
      <HotelSmsContent />
    </>
  )
}

export {HotelSms}
